// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type RebalanceTrade } from '@shared/data-access/interfaces/api/ai/portfolio-rebalance';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

const CASH_TICKERS = ['EUR', '$CASH'] as const;

/** Checks if a ticker represents cash/currency (not a real asset) */
export const isCashTicker = (ticker: string | null | undefined): boolean => {
  if (!ticker) return false;
  return CASH_TICKERS.includes(ticker as (typeof CASH_TICKERS)[number]);
};

/**
 * Gets the primary ticker from a RebalanceTrade based on operation type.
 *
 * - BUY: to_ticker (asset being bought)
 * - SELL: from_ticker (asset being sold)
 * - TRANSFER: to_ticker (destination asset)
 */
export const getPrimaryTicker = (trade: RebalanceTrade): string | undefined => {
  if (trade.operation === OperationType.BUY) {
    return trade.to_ticker;
  }
  if (trade.operation === OperationType.SELL) {
    return trade.from_ticker;
  }
  if (trade.operation === OperationType.TRANSFER) {
    return trade.to_ticker;
  }
  return trade.ticker;
};

/**
 * Extracts all unique asset tickers from an array of RebalanceTrade. Filters out
 * cash tickers (EUR, $CASH) and null/undefined values.
 */
export const extractAssetTickers = (trades: RebalanceTrade[]): string[] => {
  const tickers = new Set<string>();

  trades.forEach(trade => {
    // For TRANSFER, extract both from and to tickers
    if (trade.operation === OperationType.TRANSFER) {
      if (trade.from_ticker && !isCashTicker(trade.from_ticker)) {
        tickers.add(trade.from_ticker);
      }
      if (trade.to_ticker && !isCashTicker(trade.to_ticker)) {
        tickers.add(trade.to_ticker);
      }
      return;
    }

    // For SELL, extract from_ticker
    if (trade.operation === OperationType.SELL) {
      if (trade.from_ticker && !isCashTicker(trade.from_ticker)) {
        tickers.add(trade.from_ticker);
      }
      return;
    }

    // For BUY, extract to_ticker
    if (trade.operation === OperationType.BUY) {
      if (trade.to_ticker && !isCashTicker(trade.to_ticker)) {
        tickers.add(trade.to_ticker);
      }
      return;
    }

    // Fallback to ticker field
    if (trade.ticker && !isCashTicker(trade.ticker)) {
      tickers.add(trade.ticker);
    }
  });

  return Array.from(tickers);
};
